import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Briefcase, Users, Video, BarChart3, TrendingUp, Brain,
  ChevronRight, Clock, FileText, Plus, ArrowUpRight, Zap, Globe2, Pencil
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from 'recharts';
import { getJobs, getCandidates, getInterviewHistory } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import ATSJobImportModal from '../components/ATSJobImportModal';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-foreground">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          {p.name}: <span className="font-semibold text-foreground">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const CHART_COLORS = ['#6366f1', '#22c55e', '#eab308', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

const NewJobDropdown = ({ navigate, region, onATSImported }) => {
  const [open, setOpen] = React.useState(false);
  const [showATSImport, setShowATSImport] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <div className="relative" ref={ref}>
        <Button onClick={() => setOpen(!open)} data-testid="dashboard-new-job-btn">
          <Plus className="h-4 w-4 mr-2" /> New Job
        </Button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-52 bg-background border rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in-0 zoom-in-95">
            <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left"
              onClick={() => { setOpen(false); setShowATSImport(true); }}>
              <Globe2 className="h-4 w-4 text-orange-500" />
              <div><p className="text-sm font-medium">Add from ATS</p><p className="text-[10px] text-muted-foreground">Import from Ceipal</p></div>
            </button>
            <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left"
              onClick={() => { setOpen(false); navigate('/jobs?new=1'); }}>
              <Pencil className="h-4 w-4 text-indigo-500" />
              <div><p className="text-sm font-medium">Add Manually</p><p className="text-[10px] text-muted-foreground">Create from scratch</p></div>
            </button>
          </div>
        )}
      </div>
      <ATSJobImportModal isOpen={showATSImport} onClose={() => setShowATSImport(false)} region={region} onImported={onATSImported} />
    </>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { region, regionLabel } = useRegion();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState({ totalJobs: 0, totalCandidates: 0, totalInterviews: 0, avgScore: 0 });
  const [scoreDistribution, setScoreDistribution] = useState([]);
  const [fitBreakdown, setFitBreakdown] = useState([]);
  const [jobCandidateCounts, setJobCandidateCounts] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [region]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [jobsData, interviewData] = await Promise.all([
        getJobs(region),
        getInterviewHistory('', 'all', 200, 0, region)
      ]);
      setJobs(jobsData);
      const sessions = interviewData.sessions || [];
      setInterviews(sessions);

      // Load candidates for the latest jobs that have any
      let allCandidates = [];
      for (const job of jobsData.filter(j => j.candidate_total > 0).slice(0, 10)) {
        try {
          const cands = await getCandidates(job.id);
          allCandidates = [...allCandidates, ...cands.map(c => ({ ...c, job_title: job.title }))];
        } catch (e) { /* skip */ }
      }

      // Compute stats
      const analyzed = allCandidates.filter(c => c.analysis);
      const scores = analyzed.map(c => c.final_score || 0).filter(s => s > 0);
      const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

      setStats({
        totalJobs: jobsData.length,
        totalCandidates: jobsData.reduce((n, j) => n + (j.candidate_total || 0), 0),
        totalInterviews: sessions.length,
        avgScore
      });

      // Score distribution
      const buckets = [
        { range: '0-20', count: 0 }, { range: '21-40', count: 0 },
        { range: '41-60', count: 0 }, { range: '61-80', count: 0 },
        { range: '81-100', count: 0 }
      ];
      scores.forEach(s => {
        if (s <= 20) buckets[0].count++;
        else if (s <= 40) buckets[1].count++;
        else if (s <= 60) buckets[2].count++;
        else if (s <= 80) buckets[3].count++;
        else buckets[4].count++;
      });
      setScoreDistribution(buckets);

      // Fit breakdown
      const fitMap = {};
      analyzed.forEach(c => {
        const label = c.fit_label || 'Unrated';
        fitMap[label] = (fitMap[label] || 0) + 1;
      });
      setFitBreakdown(Object.entries(fitMap).map(([name, value]) => ({ name, value })));

      // Job candidate counts
      setJobCandidateCounts(
        jobsData.slice(0, 8).map(j => ({
          name: j.title?.substring(0, 20) || 'Untitled',
          candidates: j.total_resumes || 0
        }))
      );
    } catch (e) {
      console.error('Dashboard load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const completedInterviews = interviews.filter(i => i.status === 'completed').length;
  const pendingInterviews = interviews.filter(i => i.status === 'pending').length;
  const inProgressInterviews = interviews.filter(i => i.status === 'in_progress').length;

  const interviewStatusData = [
    { name: 'Completed', value: completedInterviews },
    { name: 'In Progress', value: inProgressInterviews },
    { name: 'Pending', value: pendingInterviews },
  ].filter(d => d.value > 0);

  const gridColor = theme === 'dark' ? '#1e293b' : '#e2e8f0';
  const axisColor = theme === 'dark' ? '#64748b' : '#94a3b8';

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {regionLabel} recruitment pipeline at a glance
          </p>
        </div>
        <NewJobDropdown navigate={navigate} region={region} onATSImported={() => { loadDashboardData(); }} />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="group hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" onClick={() => navigate('/jobs')} data-testid="stat-total-jobs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Jobs</p>
                <p className="text-3xl font-bold mt-1 tracking-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.totalJobs}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-indigo-500" strokeWidth={1.5} />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
              <span>View all</span>
              <ArrowUpRight className="h-3 w-3" />
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" onClick={() => navigate('/candidates')} data-testid="stat-total-candidates">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Candidates</p>
                <p className="text-3xl font-bold mt-1 tracking-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.totalCandidates}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-emerald-500" strokeWidth={1.5} />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
              <span>View all</span>
              <ArrowUpRight className="h-3 w-3" />
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" onClick={() => navigate('/interviews')} data-testid="stat-total-interviews">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Interviews</p>
                <p className="text-3xl font-bold mt-1 tracking-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.totalInterviews}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <Video className="h-6 w-6 text-cyan-500" strokeWidth={1.5} />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-emerald-500">
              <span>{completedInterviews} completed</span>
            </div>
          </CardContent>
        </Card>

        <Card className="group hover:-translate-y-0.5 transition-all duration-200" data-testid="stat-avg-score">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Score</p>
                <p className="text-3xl font-bold mt-1 tracking-tight" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.avgScore}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
              <span>Across all candidates</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Distribution */}
        <Card data-testid="chart-score-distribution">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BarChart3 className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
              Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scoreDistribution.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={scoreDistribution} barSize={36}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis dataKey="range" tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Candidates" radius={[6, 6, 0, 0]}>
                    {scoreDistribution.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                No score data yet. Analyze resumes to see distribution.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fit Breakdown Pie */}
        <Card data-testid="chart-fit-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Brain className="h-4 w-4 text-cyan-500" strokeWidth={1.8} />
              Candidate Fit Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fitBreakdown.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie data={fitBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={50} paddingAngle={2}>
                      {fitBreakdown.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {fitBreakdown.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                No candidate analysis data yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Interview Funnel */}
        <Card data-testid="chart-interview-funnel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Video className="h-4 w-4 text-cyan-500" strokeWidth={1.8} />
              Interview Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {interviewStatusData.length > 0 ? (
              <div className="space-y-4">
                {[
                  { label: 'Completed', count: completedInterviews, color: 'bg-emerald-500', total: stats.totalInterviews },
                  { label: 'In Progress', count: inProgressInterviews, color: 'bg-amber-500', total: stats.totalInterviews },
                  { label: 'Pending', count: pendingInterviews, color: 'bg-slate-400', total: stats.totalInterviews },
                ].map((item) => (
                  <div key={item.label} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{item.count}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${item.color} transition-all duration-700`}
                        style={{ width: `${item.total > 0 ? (item.count / item.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[140px] flex items-center justify-center text-sm text-muted-foreground">
                No interviews yet.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Jobs */}
        <Card className="lg:col-span-2" data-testid="recent-jobs">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Briefcase className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
                Recent Jobs
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} data-testid="view-all-jobs-btn">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {jobs.length > 0 ? (
              <div className="space-y-2">
                {jobs.slice(0, 5).map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:border-indigo-500/30 cursor-pointer transition-all group"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    data-testid={`recent-job-${job.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-indigo-500" strokeWidth={1.8} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{job.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{job.job_type || 'N/A'}</Badge>
                          <span className="text-xs text-muted-foreground">{job.total_resumes || 0} resumes</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center text-sm text-muted-foreground gap-3">
                <Briefcase className="h-10 w-10 opacity-30" />
                <p>No jobs created yet</p>
                <Button size="sm" onClick={() => navigate('/jobs?new=1')}>
                  <Plus className="h-4 w-4 mr-1" /> Create First Job
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
